"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePickerStatus = void 0;
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const LabeledInputFieldWithImageWidget_1 = tslib_1.__importStar(require("../widget/LabeledInputFieldWithImageWidget"));
const flatpickr_1 = tslib_1.__importDefault(require("flatpickr"));
const da_js_1 = require("flatpickr/dist/l10n/da.js");
const KeyCode = tslib_1.__importStar(require("../util/KeyboardEventCodes"));
const InputEventUtil_1 = tslib_1.__importDefault(require("../util/InputEventUtil"));
var DatePickerStatus;
(function (DatePickerStatus) {
    DatePickerStatus["OPEN"] = "OPEN";
    DatePickerStatus["CLOSED"] = "CLOSED";
})(DatePickerStatus = exports.DatePickerStatus || (exports.DatePickerStatus = {}));
/**
 * Allows for partial matching of the forms:
 * ddmmyyyy
 * dd-mm-yyyy
 * dd/mm/yyyy
 * dd.mm.yyyy
 */
const dateFormatRegex = /^(\d{2}[\-\/.]?){2}\d{4}$/;
const separatorsRegex = /[\-\/.]/g;
// Component rendered in the user interface
// <div class="date-field" style="display:flex; flex-direction:column">
//   <label for="input-id">label-text</label>
//   <div style="display: flex; flex-direction: row">
//     <input id="input-id" type="text">
//     <src="/fmk/styles/images/calendar.svg" alt="image-description">
//   </div>
//   <small optional>hint-text</small>
//   <div class="pikaday-container"></div>
// </div>
class WCAGDateField extends LabeledInputFieldWithImageWidget_1.default {
    constructor(args) {
        super({
            ...args,
            fieldSizeClass: args.fieldSizeClass || "field-size-date",
            hint: args.hint !== undefined ? args.hint : "(dd-mm-åååå)",
            imageAlt: "Tryk på ikonet for at vælge en dato",
            imagePosition: LabeledInputFieldWithImageWidget_1.ImagePosition.AFTER,
            imageSrc: fmko_ts_common_1.ImageSrcWithVersion.getImage(fmko_ts_common_1.ImageSrc.CALENDAR),
            overrideEventMouseClick: event => {
                if (!this.datePicker?.isOpen) {
                    if (event.target !== this.fieldElement && event.target !== this.imageWidget.element) {
                        event.stopPropagation();
                        this.fieldElement.click();
                    }
                }
            }
        });
        this.datePickerStatusHandlerManager = new fmko_ts_common_1.HandlerManager();
    }
    addInputClasses(...classes) {
        this.fieldElement.classList.add(...classes);
    }
    removeInputClasses(...classes) {
        this.fieldElement.classList.remove(...classes);
    }
    setValue(value, fireEvents = false) {
        if (value !== this.value) {
            this.oldValue = value;
            if (value) {
                this.datePicker.setDate(value, false);
            }
            else {
                this.datePicker.clear(false);
            }
            super.setValue(value, fireEvents);
        }
    }
    clearValue(fireEvents = false) {
        this.oldValue = undefined;
        super.clearValue(fireEvents);
    }
    isValidInputString(value) {
        if (!value) {
            return false;
        }
        if (value.length === 10) {
            return dateFormatRegex.test(value) && (0, fmko_ts_common_1.isValidDate)(value);
        }
        return super.isValidInputString(value);
    }
    addDatePickerStatusChangedHandler(handler) {
        return this.datePickerStatusHandlerManager.addHandler(handler);
    }
    openDatePicker() {
        this.datePicker.open();
        this.datePicker.element.focus();
        this.jumpToRelevantDate();
    }
    closeDatePicker() {
        this.datePicker.close();
    }
    /**
     * Set minimum date for the datepicker. Dates before minDate will be disabled and datepicker will open on the first available date
     * (if it has no current value). Will not open on dates in the past.\n
     * NOTE: Setting minDate will not work if it invalidates the current value. In this case minDate is set to defaultDate or null.
     * If defaultDate is also later than the current value, minDate is set to null (no minDate).
     */
    setMinDate(minDate, defaultDate) {
        // Only set minDate if it does not invalidate current value. Else set to defaultDate/null
        const invalidMinDate = minDate && this.getValue() && (0, fmko_ts_common_1.getMoment)(minDate).isAfter(this.getValue(), "day");
        if (invalidMinDate) {
            const invalidDefaultDate = defaultDate && this.getValue() && (0, fmko_ts_common_1.getMoment)(defaultDate).isAfter(this.getValue(), "day");
            this.datePicker.set("minDate", invalidDefaultDate ? null : this.getStartOfDay(defaultDate));
        }
        else {
            this.datePicker.set("minDate", this.getStartOfDay(minDate));
        }
    }
    setFieldElementValue(value) {
        // Update field value
        if (value === null || value === undefined) {
            this.fieldElement.value = "";
        }
        else {
            this.fieldElement.value = (0, fmko_ts_common_1.formatDate)(value);
        }
    }
    parseFieldElementValue(inputValue) {
        if (!this.isValidInputString(inputValue)) {
            return undefined;
        }
        inputValue.replace(separatorsRegex, "-");
        return (0, fmko_ts_common_1.parseDate)(inputValue);
    }
    decorateLabeledInputFieldWithImageWidgetElement(args) {
        const inputImageField = this.element;
        this.element = document.createElement("div");
        this.element.classList.add("date-field");
        this.fieldElement.maxLength = 10;
        this.add(this.labelElement);
        this.add(inputImageField);
        if (this.hintElement) {
            this.add(this.hintElement);
        }
        flatpickr_1.default.localize(da_js_1.Danish);
        this.datePicker = (0, flatpickr_1.default)(this.fieldElement, {
            allowInput: true,
            allowInvalidPreload: true,
            dateFormat: "d-m-Y",
            locale: da_js_1.Danish,
            disableMobile: true,
            maxDate: this.getStartOfDay(args.maxDate),
            minDate: this.getStartOfDay(args.minDate),
            clickOpens: false,
            parseDate: (dateString, dateFormat) => this.parseInputDate(dateString),
            formatDate: (dateObj, formatStr) => (0, fmko_ts_common_1.formatDate)(dateObj),
            errorHandler: (e) => this.silentErrorHandler(),
            onChange: (selectedDates) => {
                const [firstDate] = selectedDates;
                if (firstDate && this.oldValue !== firstDate) {
                    this.oldValue = firstDate;
                    // Super.setValue because this.setValue also updates datePicker (For external setValue calls to work)
                    super.setValue(this.oldValue, true);
                }
            },
            onOpen: () => this.datePickerStatusHandlerManager.fireEvent(new fmko_ts_common_1.ValueChangeEvent(DatePickerStatus.OPEN)),
            onClose: () => this.datePickerStatusHandlerManager.fireEvent(new fmko_ts_common_1.ValueChangeEvent(DatePickerStatus.CLOSED)),
            static: true
        });
        if (args.position === "below left") {
            const iconWidth = 31.5;
            const datePickerWidth = -307.875;
            if (this.datePicker.calendarContainer) {
                // flatpickr position doesn't work if static:true
                this.datePicker.calendarContainer.style.left = `calc(100% + ${datePickerWidth}px + ${iconWidth}px`;
            }
        }
        this.datePicker.element.addEventListener("click", event => {
            if (this.datePicker.isOpen) {
                this.datePicker.close();
            }
            else {
                this.openDatePicker();
            }
        });
        this.datePicker.element.addEventListener("keydown", event => {
            if (KeyCode.isSpace(event)) {
                this.openDatePicker();
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            if (KeyCode.isEscape(event)) {
                if (this.datePicker.isOpen) {
                    this.closeDatePicker();
                    event.stopImmediatePropagation();
                    event.stopPropagation();
                    this.fieldElement.focus();
                }
                event.preventDefault();
                return;
            }
            if (!KeyCode.isModifierKey(event)) {
                let currentElement = this.datePicker.selectedDateElem ?? this.datePicker.todayDateElem;
                if (KeyCode.isArrowDown(event)) {
                    if (!this.datePicker.isOpen) {
                        this.datePicker.open();
                    }
                    event.preventDefault();
                    currentElement.focus();
                }
            }
        });
        this.datePicker.calendarContainer?.addEventListener("keydown", event => {
            if (KeyCode.isEscape(event)) {
                this.fieldElement.focus();
                event.stopImmediatePropagation();
                event.stopPropagation();
            }
        });
    }
    isValidNextChar(_currentValue, char, _selectionStart = null, _selectionEnd = null) {
        return /\d/.test(char) || separatorsRegex.test(char);
    }
    onInput(event) {
        // User deleted field content => jump to today
        if (InputEventUtil_1.default.isDeletingText(event) && !this.fieldElement.value.length) {
            this.datePicker.jumpToDate("today");
        }
        if (!InputEventUtil_1.default.isInsertingText(event)) {
            return;
        }
        this.fieldElement.value = this.formatInputString(this.fieldElement.value);
        if (this.fieldElement.value.length === 10 && this.isValidInputString(this.fieldElement.value)) {
            this.datePicker.jumpToDate(this.fieldElement.value);
        }
    }
    onBlur() {
        // Handle when field is cleared
        if (!this.fieldElement.value && this.oldValue) {
            this.oldValue = undefined;
            this.fireEvent(new fmko_ts_common_1.ValueChangeEvent(this.oldValue));
        }
    }
    silentErrorHandler() {
        // Prevents spamming console for invalid dates
    }
    jumpToRelevantDate() {
        if (this.value) {
            return;
        }
        if (this.shouldJumpToMinDate()) {
            this.datePicker.jumpToDate(this.datePicker.config.minDate);
        }
    }
    shouldJumpToMinDate() {
        const minDate = this.datePicker.config.minDate;
        return minDate && (0, fmko_ts_common_1.getMoment)().isSameOrBefore(minDate, "day"); // Dont jump to the past
    }
    parseInputDate(dateString) {
        return dateString?.length === 10 && (0, fmko_ts_common_1.parseDate)(dateString);
    }
    formatInputString(value) {
        if (!value) {
            return "";
        }
        const strippedValue = value.split(separatorsRegex).join("");
        let stringBuilder = "";
        // Building the string from behind
        if (strippedValue.length >= 4) {
            // -YYYY
            stringBuilder = "-" + strippedValue.substring(4);
        }
        if (strippedValue.length >= 2) {
            // DD-MM
            stringBuilder = strippedValue.substring(0, 2) + "-" + strippedValue.substring(2, 4) + stringBuilder;
        }
        else {
            // DD
            stringBuilder = strippedValue;
        }
        return stringBuilder;
    }
    getStartOfDay(date) {
        if (!date) {
            return date;
        }
        const startOfDate = new Date(date);
        startOfDate.setHours(0, 0, 0, 0);
        return startOfDate;
    }
}
exports.default = WCAGDateField;
//# sourceMappingURL=WCAGDateField.js.map